// JSON エクスポート/インポート（§8）。schemaVersion 検証＋リーグ不一致ガード。
import { saveSet } from './persist';
import { SCHEMA_VERSION } from './schema';
import type { FarmSet } from './schema';

export function exportSets(sets: FarmSet[]): string {
  return JSON.stringify({ schemaVersion: SCHEMA_VERSION, exportedAt: Date.now(), sets }, null, 2);
}

export function exportSet(set: FarmSet): string {
  return exportSets([set]);
}

export interface ImportOptions {
  /** 取込先リーグ（§8: 取込先を明示させる）。 */
  targetLeague: string;
  /** true なら別リーグのセットを取込先リーグへ移し替える。false（既定）なら不一致は拒否。 */
  reassign?: boolean;
  /** false ならバリデーションのみで localStorage へ書かない（テスト・プレビュー用）。 */
  persist?: boolean;
}

export interface ImportResult {
  imported: FarmSet[];
  errors: string[];
}

function extractSets(parsed: unknown): unknown[] | null {
  if (Array.isArray(parsed)) return parsed;
  if (parsed && typeof parsed === 'object') {
    const obj = parsed as Record<string, unknown>;
    if (Array.isArray(obj.sets)) return obj.sets;
    if ('id' in obj && 'costLines' in obj) return [parsed]; // 単一セット
  }
  return null;
}

function validateSet(raw: unknown): { set: FarmSet } | { error: string } {
  if (!raw || typeof raw !== 'object') return { error: 'セットがオブジェクトでない' };
  const o = raw as Record<string, unknown>;
  if (o.schemaVersion !== SCHEMA_VERSION) {
    return { error: `schemaVersion 不一致（${String(o.schemaVersion)} ≠ ${SCHEMA_VERSION}）` };
  }
  const required = ['id', 'name', 'league', 'displayUnit', 'time', 'steady'];
  for (const key of required) {
    if (!(key in o)) return { error: `必須フィールド「${key}」が無い` };
  }
  if (!Array.isArray(o.costLines) || !Array.isArray(o.dropLines)) {
    return { error: 'costLines / dropLines が配列でない' };
  }
  // samples 行（§5.2.3）は CLI 専用の入力経路。Web には表示手段が無いため黙って壊さず明示拒否する。
  if (o.dropLines.some((d) => (d as { kind?: unknown } | null)?.kind === 'samples')) {
    return { error: `「${String(o.name)}」は CLI 専用の samples 行を含むため Web には取込めません` };
  }
  return { set: raw as FarmSet };
}

export function importSets(json: string, opts: ImportOptions): ImportResult {
  const errors: string[] = [];
  let parsed: unknown;
  try {
    parsed = JSON.parse(json);
  } catch {
    return { imported: [], errors: ['JSON 解析に失敗'] };
  }
  const rawSets = extractSets(parsed);
  if (rawSets == null) return { imported: [], errors: ['ファーム設定が見つからない'] };

  const imported: FarmSet[] = [];
  for (const raw of rawSets) {
    const v = validateSet(raw);
    if ('error' in v) {
      errors.push(v.error);
      continue;
    }
    let s = v.set;
    if (s.league !== opts.targetLeague) {
      if (opts.reassign) s = { ...s, league: opts.targetLeague };
      else {
        errors.push(`「${s.name}」は別リーグ（${s.league}）です。取込先リーグを明示してください`);
        continue;
      }
    }
    if (opts.persist !== false) s = saveSet(s);
    imported.push(s);
  }
  return { imported, errors };
}
